import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useUser,
  useDeleteUser,
} from '../../services/httpServices/queries';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  UserCircle,
  AtSign,
  Mail,
  Phone,
  Building2,
  Briefcase,
  Shield,
  KeyRound,
  Clock,
  Edit3,
  Trash2,
  Stethoscope,
  CheckCircle,
  XCircle,
  Lock,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { usePermissions } from '../../hooks/usePermissions';
import { getUserEditPath } from '../../utils/user';
import { ChangeUsernameModal } from '../../components/ui/ChangeUsernameModal';
import { ChangePasswordModal } from '../../components/ui/ChangePasswordModal';

function getRoleLabel(role: string) {
  return role
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function getUserTypeInfo(user: any) {
  if (user?.employee?.type === 'doctor') {
    return { label: 'Doctor', icon: Stethoscope, color: 'text-primary bg-primary/10' };
  }
  return { label: 'Employee', icon: Briefcase, color: 'text-secondary bg-secondary/10' };
}

export function UserView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useUser(id || '');
  const deleteUser = useDeleteUser();
  const { hasPermission, hasAccess } = usePermissions();
  const canUpdate = hasPermission('users:update');
  const canDelete = hasPermission('users:delete');

  const user = data?.data;

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteUser.mutateAsync(id);
      navigate('/users');
    } catch {
      // handled by mutation
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
        <AlertCircle size={18} />
        <span>Failed to load user details. Please try again.</span>
      </div>
    );
  }

  const typeInfo = getUserTypeInfo(user);
  const TypeIcon = typeInfo.icon;

  return (
    <div className="w-full space-y-6">
      {/* Top Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/users')}
          className="inline-flex items-center gap-2 text-text-muted hover:text-text transition-colors text-sm cursor-pointer"
        >
          <ArrowLeft size={18} />
          Back to Users
        </button>
        <div className="flex items-center gap-3">
          {canUpdate && (
            <button
              onClick={() => navigate(`/users/${id}/roles`)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/10 text-secondary rounded-lg hover:bg-secondary/20 transition-colors text-sm font-medium"
            >
              <KeyRound size={16} />
              Manage Roles
            </button>
          )}
          {canUpdate && (
            <button
              onClick={() => setShowUsernameModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/10 text-secondary rounded-lg hover:bg-secondary/20 transition-colors text-sm font-medium"
            >
              <AtSign size={16} />
              Change Username
            </button>
          )}
          {canUpdate && (
            <button
              onClick={() => setShowPasswordModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/10 text-secondary rounded-lg hover:bg-secondary/20 transition-colors text-sm font-medium"
            >
              <Lock size={16} />
              Change Password
            </button>
          )}
          {canUpdate && (
            <button
              onClick={() => navigate(getUserEditPath(user, hasAccess), { state: { from: `/users/${id}` } })}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors text-sm font-medium"
            >
              <Edit3 size={16} />
              Edit
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-danger/10 text-danger rounded-lg hover:bg-danger/20 transition-colors text-sm font-medium"
            >
              <Trash2 size={16} />
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-surface rounded-xl border border-border p-6 max-w-sm w-full space-y-4">
            <h3 className="text-lg font-semibold text-text">Confirm Delete</h3>
            <p className="text-sm text-text-muted">
              Are you sure you want to delete <strong>{user.firstName} {user.lastName}</strong>? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-text-muted hover:text-text transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteUser.isPending}
                className="px-4 py-2 bg-danger text-white rounded-lg hover:bg-danger-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {deleteUser.isPending && <Loader2 size={16} className="animate-spin" />}
                <Trash2 size={16} />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Username Modal */}
      {showUsernameModal && (
        <ChangeUsernameModal
          user={user}
          onClose={() => setShowUsernameModal(false)}
        />
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <ChangePasswordModal
          user={user}
          onClose={() => setShowPasswordModal(false)}
        />
      )}

      {/* Profile Header Card */}
      <div className="bg-surface border border-border rounded-xl p-6">
        <div className="flex flex-col md:flex-row gap-6 items-start">
          <div className="shrink-0">
            <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
              <UserCircle size={48} className="text-primary/40" />
            </div>
          </div>
          <div className="flex-1 min-w-0 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-text">{user.firstName} {user.lastName}</h1>
                <p className="text-sm text-text-muted mt-1">@{user.username}</p>
              </div>
              <span
                className={cn(
                  'inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium shrink-0',
                  user.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                )}
              >
                {user.isActive ? <CheckCircle size={12} /> : <XCircle size={12} />}
                {user.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <TypeIcon size={16} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs text-text-muted">Type</p>
                  <p className="text-sm font-medium text-text">{typeInfo.label}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <AtSign size={16} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs text-text-muted">Username</p>
                  <p className="text-sm font-medium text-text">{user.username}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Mail size={16} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs text-text-muted">Email</p>
                  <p className="text-sm font-medium text-text">{user.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Phone size={16} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs text-text-muted">Mobile</p>
                  <p className="text-sm font-medium text-text">{user.mobile || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Building2 size={16} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs text-text-muted">Department</p>
                  <p className="text-sm font-medium text-text">{user.employee?.department?.name || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Briefcase size={16} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs text-text-muted">Designation</p>
                  <p className="text-sm font-medium text-text">{user.employee?.designation?.name || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Shield size={16} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs text-text-muted">System Roles</p>
                  <div className="flex flex-wrap gap-1">
                    {user.roles?.map((role: string) => (
                      <span key={role} className="px-1.5 py-0.5 rounded text-[0.625rem] font-medium bg-primary/10 text-primary">
                        {getRoleLabel(role)}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Clock size={16} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs text-text-muted">Created</p>
                  <p className="text-sm font-medium text-text">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>

            {user.customRoles && user.customRoles.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-text-muted mb-2">Custom Roles</p>
                <div className="flex flex-wrap gap-2">
                  {user.customRoles.map((role: any) => (
                    <span key={role.id} className="px-2 py-1 rounded-lg text-xs bg-secondary/10 text-secondary border border-border">
                      {role.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Actions */}
      {canUpdate && (
        <div className="flex flex-wrap items-center justify-end gap-3 pt-4">
          <button
            onClick={() => navigate(`/users/${id}/roles`)}
            className="px-6 py-2 bg-secondary text-white rounded-lg hover:bg-secondary-dark transition-colors text-sm font-medium flex items-center gap-2"
          >
            <KeyRound size={16} />
            Manage Roles
          </button>
          <button
            onClick={() => setShowUsernameModal(true)}
            className="px-6 py-2 bg-secondary/10 text-secondary rounded-lg hover:bg-secondary/20 transition-colors text-sm font-medium flex items-center gap-2"
          >
            <AtSign size={16} />
            Change Username
          </button>
          <button
            onClick={() => setShowPasswordModal(true)}
            className="px-6 py-2 bg-secondary/10 text-secondary rounded-lg hover:bg-secondary/20 transition-colors text-sm font-medium flex items-center gap-2"
          >
            <Lock size={16} />
            Change Password
          </button>
          <button
            onClick={() => navigate(getUserEditPath(user, hasAccess), { state: { from: `/users/${id}` } })}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
          >
            <Edit3 size={16} />
            Edit User
          </button>
        </div>
      )}
    </div>
  );
}
